import { createContext, useContext } from 'react'
import { Button } from '@/components/ui/Button'
import { useToastOptional } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { describeError } from '@/lib/queryError'
import { typoNode } from '@/lib/typo'

/** A page whose first failed section already explains the cause sets this for the later
 *  sections (their ErrorState becomes one line): System, where every card has its own request. */
export const CompactErrors = createContext(false)

/**
 * A request that failed (design §6.10, WP-05). It never looks like «нет данных»: a `title-2`
 * heading with ✕ («Не удалось загрузить выплаты»), the cause in body ink-2 (52ch), a secondary
 * «Повторить» and the code in caption ink-3 that copies on click (support needs it, it is not
 * the main thing). State = colour + glyph + words (rule 21).
 */
export function ErrorState({
  title,
  error,
  onRetry,
  retrying,
  as: Tag = 'h3',
  compact,
  className,
}: {
  title: string
  error: unknown
  onRetry?: () => void
  retrying?: boolean
  as?: 'h2' | 'h3'
  /** One line — ✕ title, «Повторить», the code — for a second failed section on a page whose
   *  first failure already explains the cause (a wall of identical blocks says nothing more). */
  compact?: boolean
  className?: string
}) {
  const toast = useToastOptional()
  const compactByPage = useContext(CompactErrors)
  const { cause, code } = describeError(error)

  async function copy() {
    try {
      await navigator.clipboard.writeText(`${title}: код ${code}`)
      toast?.success('Скопировано')
    } catch {
      toast?.error('Не удалось скопировать: выделите код вручную')
    }
  }

  const codeButton = (
    <button
      type="button"
      onClick={() => void copy()}
      title="Скопировать код для поддержки"
      className="tnum rounded-xs text-caption text-ink-3 hover:text-ink-2"
    >
      Код {code}
    </button>
  )

  if (compact ?? compactByPage) {
    return (
      <div role="alert" className={cn('flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3', className)}>
        <Tag className="min-w-0 text-body font-semibold text-ink-1 [overflow-wrap:anywhere]">
          <span aria-hidden className="mr-2 text-danger">
            ✕
          </span>
          {typoNode(title)}
        </Tag>
        {onRetry && (
          <Button variant="secondary" size="sm" loading={retrying} onClick={onRetry}>
            Повторить
          </Button>
        )}
        {codeButton}
      </div>
    )
  }

  return (
    <div role="alert" className={cn('flex flex-col items-center gap-3 px-4 py-10 text-center', className)}>
      <Tag className="text-title-2 font-semibold text-ink-1 [overflow-wrap:anywhere]">
        <span aria-hidden className="mr-2 text-danger">
          ✕
        </span>
        {typoNode(title)}
      </Tag>
      <p className="max-w-prose text-body text-ink-2">{typoNode(cause)}</p>
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
        {onRetry && (
          <Button variant="secondary" loading={retrying} onClick={onRetry}>
            Повторить
          </Button>
        )}
        {codeButton}
      </div>
    </div>
  )
}

/** «Повторить» under a one-line load error that a presentational component words itself
 *  («✕ Отчёт не загрузился: …»): the same secondary action ErrorState offers (WP-05). */
export function RetryButton({ onRetry, retrying }: { onRetry: () => void; retrying?: boolean }) {
  return (
    <Button variant="secondary" size="sm" className="mt-3" loading={retrying} onClick={onRetry}>
      Повторить
    </Button>
  )
}
